<template>
  <div class="frontier">
    <!--
      The opening line is the tab row, the way the Scholars' is: which card
      you are looking at, and what you have left to spend. Matt, 2026-09-20:
      "establish a list of units... the player could just tap one, it would
      swipe back to the map and center on them."
    -->
    <div class="swap zone zone--loud">
      <div class="swap__tabs">
        <button data-tut="fr-map-tab" :class="{ 'swap__tab--on': page === 0 }" @click="page = 0">Map</button>
        <button data-tut="fr-units-tab" :class="{ 'swap__tab--on': page === 1 }" @click="page = 1">
          Units
          <span class="swap__count">{{ units.length }}</span>
        </button>
      </div>
      <!-- The map two ways: the island in three dimensions, or flat. -->
      <button v-if="page === 0" class="linkish swap__how" :title="flat ? 'Show the island in 3D' : 'Show a flat map'" @click="toggleFlat">{{ flat ? '3D' : 'flat' }}</button>
      <!-- What each hex makes, on demand (playtest 11). -->
      <!-- A SWITCH that looks like one (after playtest 12: nobody found the
           old "yields" link): what the land makes, and where a village can go. -->
      <button v-if="page === 0" class="map-toggle" :class="{ 'map-toggle--on': yields }" :aria-pressed="yields" title="Show what each hex makes, and where a village could go" @click="toggleYields">
        <span class="map-toggle__knob" aria-hidden="true" />yields
      </button>
      <span class="zone__count" data-tut="fr-orders" :class="{ 'zone__count--spent': !civ.workers }">
        {{ civ.workers }} action{{ civ.workers === 1 ? '' : 's' }} left
      </span>
    </div>


    <!--
      AN ENCOUNTER STOPS THE BOARD. Matt, playtest 6: "a message that stops
      them from doing other things, they have to make a choice or at least
      dismiss it." Land rising, a find, a camp or another people coming into
      sight, a unit lost, raiders abroad at dawn: each is put in front of the
      Frontier until it is dismissed. The engine writes them as `notices` on
      the log; this shows the ones this phone has not yet seen, oldest first.
    -->
    <Teleport to="body">
      <div v-if="notice" class="howto-backdrop encounter-backdrop" @click.self="dismissNotice">
        <div class="howto encounter" :class="`encounter--${notice.kind}`" role="dialog" aria-modal="true" :aria-label="notice.title" :style="notice.colour ? { '--people': notice.colour } : null">
          <div class="howto__body">
            <span class="encounter__kind">{{ KIND_WORDS[notice.kind] || 'On the frontier' }}<template v-if="noticesLeft > 1"> · {{ noticesLeft - 1 }} more</template></span>
            <h3 class="encounter__title"><span v-if="notice.colour" class="encounter__swatch" aria-hidden="true" />{{ notice.title }}</h3>
            <p class="encounter__text">{{ notice.text }}</p>
          </div>
          <div class="howto__nav">
            <span class="howto__dots" />
            <button class="primary howto__next" @click="dismissNotice">{{ noticesLeft > 1 ? 'Next' : 'Go on' }}</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!--
      The popover that says what a hex is hangs under the WHOLE pager, not
      inside a pane: the pager clips its overflow to hide the other card,
      and a popover inside it was clipped with it - which is why press and
      hold "did not work" at the table (playtest 6) even after the drift
      fix. Found in the flat map's test, 2026-09-22.
    -->
    <div class="pop-wrap">
    <div class="pager pager--frontier">
      <div class="pager__track" :style="{ transform: `translateX(${page * -50}%)` }">
        <div class="pager__pane" :class="{ 'pager__pane--away': page !== 0 }">
          <!-- Press and hold any hex and it says what it is. -->
          <div data-tut="fr-map" class="fr-map-wrap">
            <!-- North (playtest 9). The island is drawn from the table's own
                 fixed angle, so the needle is fixed too; the flat map is
                 north-up. TAP it and the island turns north-up, tap again
                 for the table's angle (playtest 11, Matt). -->
            <button type="button" class="map-north map-north--phone" :class="{ 'map-north--tap': !flat }" :style="{ transform: `rotate(${flat || northUp ? 0 : -0.6}rad)` }" aria-label="North - tap to turn the map north-up" :disabled="flat" @click="tapNorth">
              <span class="map-north__needle" aria-hidden="true" /><span class="map-north__n">N</span>
            </button>
            <TacticalMap
              :show-yields="yields"
              :burst="burst"
              v-if="flat"
              ref="scene"
              :map="mapScene"
              :labels="mapLabels"
              :civ="game.civ"
              @pick="onPick"
              @inspect="inspect"
            />
            <FrontierScene
              :show-yields="yields"
              :burst="burst"
              v-else
              ref="scene"
              :map="mapScene"
              :at="chosen"
              :labels="mapLabels"
              @pick="onPick"
              @inspect="inspect"
            />
                  <!-- The answer hangs OVER the map at the pressed hex, arrow on it
                       (playtest 11, Matt: "it'd be way better if it could pop up
                       directly at where I pointed with an arrow"): below the
                       finger in the top half, above it in the bottom half. -->
                  <div v-if="looking" class="pop pop--map" :class="{ 'pop--up': looking.up }" :style="{ '--pop-x': looking.arrow, ...(looking.up ? { bottom: looking.y } : { top: looking.y }) }" @click.stop>
              <span class="pop__arrow" aria-hidden="true" />
              <div class="pop__head">
                <strong>{{ looking.place }}</strong>
                <span class="pop__job">{{ looking.distance }}</span>
              </div>
              <span class="pop__via">{{ looking.text }}</span>
              <span class="pop__line">
                <template v-if="looking.step">
                  to walk in<Pip kind="actions" :n="looking.step" /><span aria-hidden="true">·</span>
                </template>
                <template v-if="looking.work">a day's work: {{ looking.work }}</template>
                <template v-else>nothing to work here</template>
              </span>
              <!-- What the ground PRODUCES, said as such (playtest, 2026-10-15,
                   Brian: "there's not information about production" - the
                   day's-work line read as the warband's chore). -->
              <span v-if="looking.makes" class="pop__line">a village here makes {{ looking.makes }}</span>
              <span v-if="looking.on" class="pop__line pop__line--warn">{{ looking.on }}</span>
              <div class="pop__feet">
                <button class="pop__close" @click="looking = null">Close</button>
              </div>
            </div>
          </div>
        </div>

        <!--
          The units, in order. Tap one and the board swipes to the map with
          that unit under the pin. Acting with it keeps it selected.
        -->
        <div class="pager__pane" :class="{ 'pager__pane--away': page !== 1 }">
          <ul v-if="units.length" class="units" data-tut="fr-units">
            <li v-for="u in unitRows" :key="u.id">
              <button
                class="unit"
                :class="{ 'unit--on': u.id === selected, 'unit--spent': !u.canAct }"
                @click="pickUnit(u.id)"
              >
                <!-- eslint-disable-next-line vue/no-v-html -- constant art from frontierArt.js -->
                <svg class="unit__face" viewBox="0 0 24 24" aria-hidden="true" v-html="u.art" />
                <span class="unit__body">
                  <span class="unit__name">{{ u.name }}</span>
                  <span class="unit__where">{{ u.where }}</span>
                  <span class="unit__purpose">{{ u.purpose }}</span>
                </span>
                <span class="unit__bar" :title="`strength ${u.strength}`">
                  <span
                    v-for="n in u.maxStrength"
                    :key="n"
                    class="unit__pip"
                    :class="{ 'unit__pip--out': n > u.strength }"
                  />
                </span>
              </button>
            </li>
          </ul>
          <p v-else class="phone__note phone__note--lookup">
            You have no units. The Stewards train them, at the city or at any town.
          </p>
          <!-- The peoples you have met and where you stand with them (playtest
               13: it was only on the TV's left column). -->
          <ul v-if="peoples.length" class="peoples">
            <li v-for="p in peoples" :key="p.id" class="peoples__one" :style="{ '--people': p.colour }">
              <span class="peoples__swatch" aria-hidden="true" />
              <span class="peoples__name">{{ p.name }}</span>
              <span class="peoples__stance" :class="`peoples__stance--${p.stance}`">{{ p.standing }}</span>
            </li>
            <li class="peoples__note">A people is broken by beating their war parties until their seat falls; broken and not hostile is what Dominion counts.</li>
          </ul>
          <!-- The next things the Scholars could learn for the Frontier, greyed
               (Matt, playtest 13: "show people on their board the next thing
               they could do if they had a specific technology"). -->
          <details v-if="nextTechs.length" class="phone__log next-techs">
            <summary><span class="tree__door">▸ Ask the Scholars for</span> · {{ nextTechs.length }} ideas</summary>
            <ul class="next-techs__list">
              <li v-for="t in nextTechs" :key="t.id"><b>{{ t.name }}</b> <small v-if="t.after">after {{ t.after }}</small><span>{{ t.text }}</span></li>
            </ul>
          </details>
        </div>
      </div>
    </div>
    </div>

    <!-- Who is in hand, and what a step costs them. -->
    <p class="fact">
      <span class="fact__label">{{ chosen ? chosenName : 'No unit chosen' }}</span>
      <span v-if="chosen" class="fact__bit"><Pip kind="actions" :n="1" /> a step</span>
      <span v-if="chosen && roughStep > 1" class="fact__bit">
        <Pip kind="actions" :n="roughStep" /> into rough ground
      </span>
      <!-- Hurt? Say how to heal (playtest 2026-09-25: "how do I make my
           guys healthy again"). The Stewards reinforce at the city or a town. -->
      <span v-if="chosen && chosen.kind === 'warband' && chosen.strength < warbandCap(civ)" class="fact__bit fact__heal">
        {{ chosen.strength }}/{{ warbandCap(civ) }} - Stewards reinforce ({{ reinforceGoods }} goods)
      </span>
    </p>
    <!-- What the unit is for, unless the line above is already telling you
         how to heal it (one phone screen per board). -->
    <p v-if="chosen && !(chosen.kind === 'warband' && chosen.strength < warbandCap(civ))" class="unit-purpose">{{ UNIT_PURPOSE[chosen.kind] }}</p>

    <!--
      Tapping a camp or a raider is picking a fight, and a fight is a BET -
      but an attrition fight's honest preview is the TRADE, not a win
      percentage. Both bars, before you commit.
    -->
    <template v-if="picking && chosen">
      <h4 class="zone zone--loud">
        <span class="zone__label">{{ picking.name }}</span>
        <span class="zone__count">{{ picking.rival ? `${picking.stance}, ` : '' }}strength {{ picking.strength }}</span>
      </h4>
      <p class="trade">
        <span class="trade__side">
          <span class="trade__who">yours</span>
          <span class="trade__bar"><span v-for="n in chosen.strength" :key="n" class="trade__pip" /></span>
          <span class="trade__n">deals ~{{ preview.deal }}</span>
        </span>
        <span class="trade__side trade__side--foe">
          <span class="trade__who">theirs</span>
          <span class="trade__bar"><span v-for="n in picking.strength" :key="n" class="trade__pip trade__pip--foe" /></span>
          <span class="trade__n">deals ~{{ preview.take }}</span>
        </span>
      </p>
      <!-- The rule, where the bet is made (playtest 9: "you have to roll above
           a certain number... I don't know, let's find out"; both sides fell
           at once and it read as a bug). -->
      <p v-if="!picking.rival || picking.stance" class="trade__rule">Both sides roll a die per strength, at once. Each {{ hitNumber }} or more takes one strength off the other; both can fall.</p>
      <ul class="hall__actions hall__actions--fit">
        <li v-if="!picking.rival">
          <button class="tile" :disabled="!canAct || !fightCheck.ok" @click="act('fight', { unit: chosen.id, foe: picking.id })">
            <span class="tile__head">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from frontierArt.js -->
              <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="art.fight" />
              <span class="tile__name">Fight</span>
            </span>
            <ActionCost :cost="{ workers: 1 }" as-actions />
            <span class="tile__gain">one exchange</span>
            <span v-if="!fightCheck.ok" class="action__why">{{ fightCheck.why }}</span>
          </button>
        </li>
        <li v-else>
          <button class="tile" :disabled="!canAct || !raidCheck.ok" @click="act('raid', { unit: chosen.id, foe: picking.id })">
            <span class="tile__head">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from frontierArt.js -->
              <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="art.raid" />
              <span class="tile__name">Raid</span>
            </span>
            <ActionCost :cost="{ workers: 1 }" as-actions />
            <span class="tile__gain">one exchange; they turn hostile</span>
            <span v-if="!raidCheck.ok" class="action__why">{{ raidCheck.why }}</span>
          </button>
        </li>
        <li v-if="picking.rival || picking.foe === 'camp'">
          <button class="tile" :disabled="!canAct || !parleyCheck.ok" @click="act('parley', { unit: chosen.id, foe: picking.id })">
            <span class="tile__head">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from frontierArt.js -->
              <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="art.parley" />
              <span class="tile__name">Parley</span>
            </span>
            <ActionCost :cost="parleyPrice" as-actions />
            <span class="tile__gain">{{ picking.rival ? 'their temper warms one step' : 'the camp breaks up' }}</span>
            <span v-if="!parleyCheck.ok && parleyCheck.kind !== 'cost'" class="action__why">{{ parleyCheck.why }}</span>
          </button>
        </li>
        <li>
          <button class="tile" @click="picking = null">
            <span class="tile__head"><span class="tile__name">Never mind</span></span>
            <span class="tile__gain">back to the map</span>
          </button>
        </li>
      </ul>
    </template>

    <template v-else>
      <h4 class="zone">
        <span class="zone__label">Things to do here</span>
      </h4>
      <ul v-if="doable.length || beside.length" class="hall__actions hall__actions--fit">
        <!-- Who stands beside the unit in hand, one tile each (playtest 11,
             Matt for three rounds: "why can't I parley with these guys"; the
             answer was to tap them on the map, which nothing said). -->
        <li v-for="f in beside" :key="`beside-${f.id}`">
          <button class="tile" :disabled="!canAct" @click="picking = f">
            <span class="tile__head">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from frontierArt.js -->
              <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="f.rival ? art.parley : art.fight" />
              <span class="tile__name">{{ f.rival ? 'Parley or raid' : 'Fight or parley' }}: {{ f.name }}</span>
            </span>
          </button>
        </li>
        <li v-for="a in doable" :key="a.key || a.id">
          <button class="tile" :disabled="!canAct || !a.check.ok" @click="act(a.id, { ...(chosen ? { unit: chosen.id } : {}), ...(a.params || {}) })">
            <span class="tile__head">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from frontierArt.js -->
              <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="a.art" />
              <span class="tile__name">{{ a.label }}</span>
            </span>
            <ActionCost :cost="a.cost" as-actions />
            <span class="tile__gain">
              <template v-if="a.roll">
                <Pip kind="dice" :n="a.roll.dice" />×<Pip
                  :kind="a.roll.resource"
                  :n="a.roll.low"
                  :text="`${a.roll.low}–${a.roll.high}`"
                />
              </template>
              <template v-else>{{ a.gain }}</template>
            </span>
          </button>
        </li>
      </ul>
      <p v-else-if="chosen" class="phone__note">Nothing to do where they stand. Tap gold ground to move.</p>
    </template>
  </div>
</template>

<script>
import {
  actionsForRole, checkAction, resolveCost, resolveLabel,
  huntDice as huntDiceOf, marchEase, stepInto, placeName, villageAt, parleyCost, warbandCap, hitOn, mergeTargets } from '@/engine/roleActions';
import {
  unitsOf, unitById, kindOf, stepsFrom, foesNear, stepOrders, tradePreview, describeUnit, unitName,
} from '@/engine/units';
import { UNIT_KINDS, UNIT_PURPOSE, REINFORCE_COST } from '@/engine/constants';
import { hexDistanceBetween, key as hexKey } from '@/engine/map';
import {
  groundAt, workAt, findAt, FINDS,
} from '@/engine/terrain';
import { layoutFrontier } from '@/world/frontierLayout';
import FrontierScene from './FrontierScene.vue';
import TacticalMap from './TacticalMap.vue';
import ActionCost from './ActionCost.vue';
import Pip from './Pip.vue';
import { FRONTIER_ART, artForFrontier } from './frontierArt';
import { rivalsNear, rivalAt, rivalColour, rivalsOf } from '@/engine/rivals';
import { TECHS } from '@/engine/cards';
import { civFor, logTotal, logAt } from '@/engine/game';

// The Frontier's board: two cards you swipe between. The MAP is where you
// act - tap gold ground to step, tap a camp to pick a fight - and the UNITS
// card is the list you work down. A WORKER IS ONE UNIT'S TURN, so the chip
// on the opening line counts orders, and the dawn split decides how many of
// your pieces get to do anything today.
const NAMES = { camp: 'A raider camp', den: 'A bandits\' den', wolves: 'A wolves\' den' };
const cap = (t) => (t ? t[0].toUpperCase() + t.slice(1) : t);
const ART_FOR = { warband: 'fight', scout: 'scout', pioneer: 'outpost' };

const NOTICE_KEY = 'commonwealth:frontier-notices';
const FLAT_KEY = 'commonwealth:frontier-flat';
const YIELDS_KEY = 'commonwealth:frontier-yields';
const KIND_WORDS = {
  region: 'New land', find: 'Found', camp: 'In sight', rival: 'Another people', loss: 'Lost', cleared: 'Cleared', raiders: 'In the night',
};

export default {
  name: 'FrontierBoard',
  components: {
    ActionCost, FrontierScene, TacticalMap, Pip,
  },
  props: {
    game: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    preview: { type: Boolean, default: false },
    // The outcome card is up on the phone: encounters wait their turn.
    hold: { type: Boolean, default: false },
  },
  emits: ['act'],
  data() {
    return {
      // The 3D island turned north-up by a tap on the compass (playtest 11).
      northUp: false,
      // Where a fight just happened: { q, r, key }, cleared after a beat.
      burst: null,
      burstTimer: null,
      page: 0, selected: null, picking: null, looking: null, art: FRONTIER_ART, KIND_WORDS, UNIT_PURPOSE,
      // Log index up to which notices have been dismissed on this phone.
      noticedTo: -1,
      noticedIn: 0,
      yields: (() => { try { return localStorage.getItem(YIELDS_KEY) === '1' || (import.meta.env.DEV && new URLSearchParams(window.location.search).get('yields') === '1'); } catch { return false; } })(),
      // The flat map, remembered per phone.
      // `?flat=1` in dev opens it flat, for the screenshot loop.
      flat: (() => { try { return localStorage.getItem(FLAT_KEY) === '1' || (import.meta.env.DEV && new URLSearchParams(window.location.search).get('flat') === '1'); } catch { return false; } })(),
    };
  },
  computed: {
    reinforceGoods() { return REINFORCE_COST.goods; },
    logCount() { return logTotal(this.game); },
    civ() { return civFor(this.game, 'military'); },
    canAct() { return !this.readonly && !this.preview; },
    hitNumber() { return hitOn(this.civ); },
    units() { return unitsOf(this.civ); },
    // Every notice not yet dismissed here, in the order it happened.
    pendingNotices() {
      if (!this.canAct || this.hold) return [];
      const out = [];
      // Positions are ABSOLUTE (logBase counts what a long game trimmed).
      const base = this.game.logBase || 0;
      this.game.log.forEach((l, k) => {
        const i = base + k;
        if (i <= this.noticedTo || !l.notices?.length) return;
        if (l.kind === 'act' && l.role !== 'military') return;
        // Routine "raiders are abroad" notices don't stop you; they are on
        // the map (playtest 2026-09-25: "those pop-ups are annoying").
        l.notices.forEach((n, j) => { if (n.kind !== 'raiders' && (i > this.noticedTo + 1 || j >= this.noticedIn)) out.push({ ...n, at: i, j }); });
      });
      return out;
    },
    notice() { return this.pendingNotices[0] || null; },
    peoples() {
      return rivalsOf(this.civ).filter((r) => r.met).map((r) => ({
        id: r.id, name: cap(r.name), colour: rivalColour(r), stance: r.stance,
        standing: `${r.allied ? 'ally' : r.stance}${(r.broken || 0) > 0 ? ' · broken' : ''}`,
      }));
    },
    // Military technologies not yet learned, cheapest tier first, with what
    // each still needs - the Frontier's own clause when the text is split.
    nextTechs() {
      const has = this.civ.techs || [];
      const clause = (t) => {
        const parts = t.text.split(/(?=\b(?:Frontier|Stewards|Scholars|Hall):)/).map((x) => x.trim()).filter(Boolean);
        const mine = parts.filter((x) => x.startsWith('Frontier:'));
        return (mine.length ? mine.map((x) => x.replace(/^Frontier:\s*/, '')).join(' ') : t.text);
      };
      return Object.entries(TECHS)
        .filter(([id, t]) => !has.includes(id) && (t.forRole === 'military' || (Array.isArray(t.forRole) && t.forRole.includes('military'))))
        .sort((a, b) => a[1].tier - b[1].tier)
        // Six, not four (playtest 14: "which technology lets villages defend
        // themselves" was asked fourteen times; Palisades sat past the cut).
        .slice(0, 6)
        .map(([id, t]) => ({ id, name: t.name, text: clause(t), after: (t.needs || []).filter((n) => !has.includes(n)).map((n) => TECHS[n]?.name || n).join(' and ') }));
    },
    noticesLeft() { return this.pendingNotices.length; },
    chosen() { return unitById(this.civ, this.selected) || this.units[0] || null; },
    chosenName() {
      if (!this.chosen) return '';
      return `${cap(describeUnit(this.chosen))}, ${placeName(this.civ, this.chosen.q, this.chosen.r)}`;
    },
    // The list, in order, each with where it is and whether it still has
    // anything it could do with an order.
    unitRows() {
      return this.units.map((u) => ({
        id: u.id,
        name: cap(describeUnit(u)),
        where: placeName(this.civ, u.q, u.r),
        purpose: UNIT_PURPOSE[u.kind] || '',
        strength: u.strength,
        maxStrength: Math.max(u.strength, UNIT_KINDS[u.kind]?.maxStrength || u.strength),
        art: artForFrontier(ART_FOR[u.kind] || 'fight'),
        canAct: this.canAct && this.civ.workers > 0 && this.somethingFor(u),
      }));
    },
    // What the CHOSEN unit could step to, and could afford to.
    targets() {
      const u = this.chosen;
      if (!u || !this.canAct) return [];
      return stepsFrom(this.civ, u)
        .filter(([q, r]) => stepOrders(this.civ, u, q, r) <= this.civ.workers);
    },
    roughStep() {
      if (!this.chosen || kindOf(this.chosen).ignoresRough) return 1;
      return Math.max(1, 2 - marchEase(this.civ));
    },
    mapScene() {
      return layoutFrontier(this.game.civ, {
        targets: this.targets,
        selected: this.chosen ? this.chosen.id : null,
        ease: marchEase(this.civ),
      });
    },
    mapLabels() {
      const out = [];
      // Say what a thing IS, not just a number (Matt, 2026-09-25: "I don't
      // understand what the rival labels are labeling").
      const seen = new Set(this.game.civ.seen || []);
      const sees = (q, r) => !this.game.civ.seen || seen.has(`${q},${r}`);
      (this.game.civ.menaces || []).filter((m) => sees(m.q, m.r)).forEach((m) => out.push({
        id: `menace:${m.id}`, kind: 'menace', text: `${m.kind === 'den' ? 'den' : m.kind === 'wolves' ? 'wolves' : 'camp'} · strength ${m.strength}`, title: NAMES[m.kind] || 'A camp',
      }));
      (this.game.civ.raiders || []).filter((x) => sees(x.q, x.r)).forEach((x) => out.push({
        id: `raider:${x.id}`, kind: 'menace', text: `raiders · strength ${x.strength}`, title: x.name,
      }));
      // Only peoples you have MET, and only where you can see their seat.
      (this.game.civ.rivals || []).filter((rv) => rv.met && sees(rv.q, rv.r)).forEach((rv) => out.push({
        id: `rival:${rv.id}:seat`,
        kind: 'rival',
        colour: rivalColour(rv),
        text: `${cap(rv.name)} · ${rv.stance}`,
        title: `their home - strength ${rv.power}`,
      }));
      (this.game.civ.villages || []).forEach((v) => out.push({
        id: `village:${v.id}`,
        kind: v.damaged ? 'menace' : 'town',
        text: v.damaged ? 'wrecked' : v.name,
        title: v.chartered ? 'a town' : 'a village',
      }));
      // No text over your own units: they are people now, one per strength,
      // and the one in hand stands in a gold ring (its name is in the strip
      // under the map). Matt: "I don't like the unit labels."
      return out;
    },
    fightCheck() {
      if (!this.picking || !this.chosen) return { ok: false, why: 'Nothing to fight.' };
      return checkAction(this.game, 'military', 'fight', { unit: this.chosen.id, foe: this.picking.id });
    },
    raidCheck() {
      if (!this.picking || !this.chosen) return { ok: false, why: 'Nothing to raid.' };
      return checkAction(this.game, 'military', 'raid', { unit: this.chosen.id, foe: this.picking.id });
    },
    // The camps, raiders and peoples beside the unit in hand, shaped as a
    // tap on them would shape `picking`.
    beside() {
      if (!this.chosen || this.picking) return [];
      const foes = foesNear(this.civ, this.chosen).map((f) => ({ ...f, name: f.foe === 'camp' ? (NAMES[f.kind] || 'A camp') : f.name }));
      const peoples = rivalsNear(this.civ, this.chosen).map((rv) => ({ id: rv.id, rival: true, name: cap(rv.name), strength: rv.power, stance: rv.stance }));
      return [...foes, ...peoples];
    },
    parleyCheck() {
      if (!this.picking || !this.chosen) return { ok: false, why: 'Nobody to parley with.' };
      return checkAction(this.game, 'military', 'parley', { unit: this.chosen.id, foe: this.picking.id });
    },
    parleyPrice() {
      if (!this.picking || !this.chosen) return { workers: 1 };
      return { workers: 1, coin: parleyCost(this.civ, { unit: this.chosen.id, foe: this.picking.id }) };
    },
    preview() {
      if (!this.picking || !this.chosen) return { deal: 0, take: 0 };
      return tradePreview(this.civ, this.chosen, { strength: this.picking.strength });
    },
    // Only what this unit could actually do where it stands. Anything the
    // ground or the rules forbid is simply not listed.
    doable() {
      const u = this.chosen;
      // No units: what can still be done without one (playtest 9).
      if (!u) {
        return actionsForRole('military')
          .filter((a) => a.id === 'hunt-near' || (a.id === 'build-monument' && this.civ.monument.revealed))
          .map((a) => ({
            id: a.id,
            label: resolveLabel(a, this.civ),
            gain: a.gain(this.civ),
            cost: resolveCost(a, this.civ, 1, {}, this.game),
            art: artForFrontier(a.id === 'hunt-near' ? 'forage' : a.id),
            roll: this.rollFor(a),
            check: checkAction(this.game, 'military', a.id, {}),
          }))
          .filter((a) => a.check.ok || a.check.kind === 'cost');
      }
      return actionsForRole('military')
        // Fight, raid and parley are made by tapping WHO - the camp or the
        // people - and priced there by their strength. Listed here without
        // one, parley showed a base price whenever the workers ran out (a
        // cost failure outranks the missing target) and vanished when they
        // came back (playtest 7MTZG).
        .filter((a) => !['move', 'fight', 'raid', 'parley', 'hunt-near'].includes(a.id))
        .filter((a) => a.id !== 'build-monument' || this.civ.monument.revealed)
        // A guide is worth hiring when the orders are nearly spent, not before
        // (and the tile would push the board past one screen at dawn).
        .filter((a) => a.id !== 'hire' || (this.game.meta?.phase === 'day' && (this.civ.workers || 0) <= 1))
        // Merge is one tile per warband this one could join (after playtest 12).
        .flatMap((a) => (a.id === 'merge' ? mergeTargets(this.civ, u).map((t) => ({ a, params: { unit: u.id, into: t.id }, label: `Merge into ${unitName(t)}`, gain: `${t.strength + u.strength} strong` })) : [{ a, params: { unit: u.id } }]))
        .map(({ a, params, label, gain }) => ({
          id: a.id,
          key: params.into ? `${a.id}:${params.into}` : a.id,
          params,
          label: label || resolveLabel(a, this.civ),
          gain: gain || a.gain(this.civ),
          cost: resolveCost(a, this.civ, 1, params, this.game),
          art: artForFrontier(a.id === 'merge' ? 'fight' : a.id),
          roll: this.rollFor(a),
          check: checkAction(this.game, 'military', a.id, params),
        }))
        .filter((a) => a.check.ok || a.check.kind === 'cost');
    },
  },
  watch: {
    logCount() {
      const last = this.game.log[this.game.log.length - 1];
      // A fight flashes on the map at the unit that fought (pacing, 2026-09-28).
      if (last && last.kind === 'act' && last.role === 'military' && (last.action === 'fight' || last.action === 'raid')) {
        const u = this.chosen;
        if (u) {
          this.burst = { q: u.q, r: u.r, key: Date.now() };
          clearTimeout(this.burstTimer);
          this.burstTimer = setTimeout(() => { this.burst = null; }, 1100);
        }
      }
    },
    // A unit that dies, or a board that arrives, should leave something
    // sane in hand.
    units: {
      immediate: true,
      handler(list) {
        if (!list.length) { this.selected = null; return; }
        if (!list.some((u) => u.id === this.selected)) this.selected = list[0].id;
      },
    },
  },
  mounted() {
    // Notices dismissed before a reload stay dismissed; a phone that has
    // never seen this game starts from where the log is now, so an old game
    // does not replay its whole history at it.
    const key = `${NOTICE_KEY}:${this.game.seed}`;
    let stored = null;
    try { stored = localStorage.getItem(key); } catch { stored = null; }
    this.noticedTo = stored !== null ? Number(stored) : logTotal(this.game) - 1;
  },
  methods: {
    tapNorth() {
      if (this.flat || !this.$refs.scene?.toggleNorth) return;
      this.northUp = this.$refs.scene.toggleNorth();
    },
    warbandCap,
    toggleYields() {
      this.yields = !this.yields;
      try { localStorage.setItem(YIELDS_KEY, this.yields ? '1' : '0'); } catch { /* storage off */ }
    },
    toggleFlat() {
      this.flat = !this.flat;
      this.looking = null;
      try { localStorage.setItem(FLAT_KEY, this.flat ? '1' : '0'); } catch { /* storage off */ }
    },
    dismissNotice() {
      const n = this.notice;
      if (!n) return;
      const entry = logAt(this.game, n.at);
      const last = n.j >= (entry?.notices?.length || 1) - 1;
      // Dismissing the last notice of an entry retires the entry; an earlier
      // one leaves the entry open with a count of how many are done.
      if (last) { this.noticedTo = n.at; this.noticedIn = 0; } else { this.noticedTo = n.at - 1; this.noticedIn = n.j + 1; }
      try { localStorage.setItem(`${NOTICE_KEY}:${this.game.seed}`, String(this.noticedTo)); } catch { /* storage off */ }
    },
    somethingFor(u) {
      if (stepsFrom(this.civ, u).length) return true;
      return actionsForRole('military')
        .some((a) => checkAction(this.game, 'military', a.id, { unit: u.id }).ok);
    },
    // Tapping a unit in the LIST is an explicit request to go and look at
    // it, so this one does centre the camera. Auto-advance does not - Matt,
    // 2026-09-19: "the map shouldn't move when I do that."
    pickUnit(id) {
      this.selected = id;
      this.picking = null;
      this.page = 0;
      this.$nextTick(() => {
        const u = unitById(this.civ, id);
        if (u && this.$refs.scene) this.$refs.scene.lookAt(u.q, u.r);
      });
    },
    // Each unit does a thing and the board moves on to the next one down
    // the list, so you work through them. It only PANS if the next unit is
    // off screen; it never re-centres for the sake of it.
    advance() {
      const list = this.units;
      if (list.length < 2) return;
      const at = list.findIndex((u) => u.id === this.selected);
      const after = [...list.slice(at + 1), ...list.slice(0, Math.max(0, at))];
      const next = after.find((u) => this.somethingFor(u));
      if (!next) return;
      this.selected = next.id;
      this.$nextTick(() => {
        if (this.$refs.scene) this.$refs.scene.bringIntoView(next.q, next.r);
      });
    },
    rollFor(a) {
      if (!a.dice) return null;
      const d = a.dice(this.civ);
      if (!d) return null;
      const low = d.base + 1;
      return {
        dice: d.count ?? huntDiceOf(this.civ), resource: d.resource, low, high: d.base + d.faces,
      };
    },
    onPick(hit) {
      if (!this.canAct || !hit || !this.chosen) return;
      if (hit.kind === 'march') { this.act('move', { unit: this.chosen.id, to: { q: hit.q, r: hit.r } }); return; }
      if (hit.kind === 'unit' && hit.id) { this.selected = hit.id; this.picking = null; return; }
      if (hit.kind === 'menace' || hit.kind === 'raider') {
        const foe = foesNear(this.civ, this.chosen).find((f) => f.id === hit.id);
        if (foe) { this.picking = { ...foe, name: foe.foe === 'camp' ? (NAMES[foe.kind] || 'A camp') : foe.name }; return; }
      }
      if (hit.kind === 'rival') {
        // Their ground is theirs: from BESIDE it you can raid or parley.
        const rv = rivalsNear(this.civ, this.chosen).find((x) => x.id === hit.id);
        if (rv) { this.picking = { id: rv.id, rival: true, name: cap(rv.name), strength: rv.power, stance: rv.stance }; return; }
      }
      // Out of reach: the tap still says what it is (the flat map sends the hex along).
      if (hit.q !== undefined && hit.r !== undefined) this.inspect({ q: hit.q, r: hit.r, atX: hit.atX });
    },
    inspect({ q, r, atX, atY }) {
      // Press and hold one of YOUR units and it is in hand (playtest
      // 2026-09-25: "press and hold on one of my units... and it would
      // select that unit, instead of having to switch over to the list").
      const mine = (this.civ.units || []).find((u) => u.q === q && u.r === r);
      if (mine && mine.id !== this.selected) { this.selected = mine.id; this.picking = null; this.looking = null; return; }
      const { civ } = this;
      const wrap = this.$el?.querySelector?.('.fr-map-wrap');
      const wide = wrap ? wrap.clientWidth : (this.$el ? this.$el.clientWidth : 0);
      const tall = wrap ? wrap.clientHeight : 0;
      const arrow = `${Math.round(Math.min(Math.max(atX || wide / 2, 24), Math.max(24, wide - 24)))}px`;
      // Below the finger in the top half of the map, above it in the bottom
      // half, 14px clear of the hex either way.
      const py = Number.isFinite(atY) ? atY : tall / 2;
      const up = tall > 0 && py > tall / 2;
      const y = `${Math.round(up ? tall - py + 14 : py + 14)}px`;
      const seen = civ.seen ? new Set(civ.seen) : null;
      if (seen && !seen.has(hexKey(q, r))) {
        this.looking = {
          place: 'Unseen ground', text: 'Not explored yet.', distance: '', arrow, y, up,
        };
        return;
      }
      const ground = groundAt(civ, q, r);
      const w = workAt(civ, q, r);
      const here = this.chosen;
      const d = here ? hexDistanceBetween([q, r], [here.q, here.r]) : 0;
      const m = (civ.menaces || []).find((x) => x.q === q && x.r === r);
      const x = (civ.raiders || []).find((y) => y.q === q && y.r === r);
      const find = findAt(civ, q, r);
      const v = villageAt(civ, q, r);
      const rv = rivalAt(civ, q, r);
      const on = [
        rv ? `${cap(rv.name)}' ground - ${rv.stance}, strength ${rv.power}. Raid or parley from beside it.` : null,
        m ? `${NAMES[m.kind] || 'Something'}, strength ${m.strength}` : null,
        x ? `${x.name}, strength ${x.strength}, walking at you` : null,
        v ? `${v.name}${v.chartered ? ', a town' : ''}${v.damaged ? ' - wrecked' : ''}` : null,
        find ? `${FINDS[find.kind].name}: ${FINDS[find.kind].text}` : null,
      ].filter(Boolean).join(' ');
      this.looking = {
        arrow,
        y,
        up,
        place: cap(placeName(civ, q, r)),
        text: ground.text,
        distance: d === 0 ? 'you are here' : `${d} ${d === 1 ? 'hex' : 'hexes'} away`,
        step: d === 0 || !here ? 0 : stepInto(civ, q, r),
        work: w ? `${w.label} for ${w.resource}` : '',
        makes: w ? w.resource : '',
        on,
      };
    },
    act(actionId, params = {}) {
      this.picking = null;
      this.looking = null;
      this.$emit('act', { role: 'military', actionId, params });
      // No auto-advance any more (Matt, playtest 7): keep the selection on
      // the unit he was already using.
    },
  },
};
</script>
